// Layer 3 (Transformation): a service result projected onto the shared control
// chart. No frame data or analytics value is computed here.

import type { AnalysisReplay, AnalysisRequest, AnalysisResult } from '@/shared/contracts/analytics/v1';
import type { ControlChartModel } from '@/versions/v1/modules/_viz';
import { envelopeClock, firedSignals } from '@/versions/v1/modules/advisory/AnalysisRun.transform';

type Slices = AnalysisRequest['slices'];

/** A concise, explicit state for the source that answered the replay request. */
export function analysisReplaySourceLabel(source: 'live' | 'fixture'): string {
  return source === 'live' ? 'computed by the analytics service' : 'recorded';
}

export type AnalysisReplayChart = {
  key: string;
  ruleId: string;
  segmentStart: string;
  caption: string;
  markerAt: string | null;
  model: ControlChartModel;
};

type AnalysisReplayFrame = AnalysisReplay[number];

/** The control-chart inputs carried by one analysis result. */
export function analysisResultChartProps(
  result: AnalysisResult,
  slices: Slices,
): AnalysisReplayChart[] {
  const rows = firedSignals(result, slices);
  return rows.flatMap((row) => {
    if (!row.chart) return [];
    const signal = (result.spc_signals ?? []).find(
      (candidate) =>
        candidate.fired &&
        candidate.rule_id === row.ruleId &&
        envelopeClock(candidate.segment_start) === row.segmentStart &&
        envelopeClock(candidate.segment_end) === row.segmentEnd,
    );
    const markerAt = signal?.first_event_time ? envelopeClock(signal.first_event_time) : null;

    return [
      {
        key: `${row.ruleId}-${row.segmentStart}`,
        ruleId: row.ruleId,
        segmentStart: row.segmentStart,
        caption: row.chart.caption,
        markerAt,
        model: {
          readingLabel: `${row.ruleLabel} · ${row.modeLabel}`,
          phase: 'locked',
          points: row.chart.points.map((point) => ({
            at: point.at,
            value: point.value,
            highlighted: point.at === markerAt,
          })),
          excursionRange: signal?.segment_start && signal.segment_end
            ? { startAt: signal.segment_start, endAt: signal.segment_end }
            : undefined,
          limits: {
            mean: row.chart.centre,
            ucl: row.chart.ucl,
            lcl: row.chart.lcl,
          },
          unit: row.chart.unit ?? undefined,
          precision: 2,
        },
      },
    ];
  });
}

/** Frame result projection; point availability and limits are the service's. */
export function analysisReplayChartProps(
  frame: AnalysisReplayFrame,
  slices: Slices,
): AnalysisReplayChart[] {
  return analysisResultChartProps(frame.result, slices);
}
